import { useRouter } from 'next/router';
import {
    MobileAlertPanelWrapper,
    TabletText,
    MobileText,
    Divider
} from './AlertPanelMobile.styles';
import { FC, useContext, MouseEvent } from 'react';
import {
    SearchContext,
    AnalyticsTaxonomy
} from '@zoopla/listing-search-context';
import triggerTracking from '@blueprint/tracking';
import { alertLinkClickHandler } from '@zoopla/listing-search/nav-with-auth';
import { Links } from '@zoopla/listing-search-context';
import { Icon } from '@blueprint/ui';
import { ButtonLinkWithIcon } from '@zoopla/blueprint-button-with-icon';
import { useIsLoggedIn, getUserSelector } from '@zoopla/redux-reducer-user';
import {
    Taxonomy,
    useTrackSegmentEvent,
    ANALYTICS_BASE_TAXONOMY,
    UserTaxonomy,
    updateUserTaxonomyKeys
} from '@zoopla/tracking';
import { useSelector } from 'react-redux';

const mobileCreateAlertButtonText = 'Create alert';

const tabletCreateAlertButtonText = 'Create email alert';
const mobileSaveSearchButtonText = 'Save search';
const tabletSaveSearchButtonText = 'Save this search';
const mobileTurnOnAlertButtonText = 'Turn on alerts';
const tabletTurnOnAlertButtonText = 'Turn on email alert';
const mobileEditSearchButtonText = 'Edit Search';
const tabletEditSearchButtonText = 'Edit saved search';

type AlertPanelMobileProps = {
    links: Links;
    taxonomy?: AnalyticsTaxonomy;
};

export const AlertPanelMobile: FC<
    React.PropsWithChildren<AlertPanelMobileProps>
> = ({ links, taxonomy }) => {
    const { state } = useContext(SearchContext);
    const userTaxonomy = useSelector(getUserSelector);
    const router = useRouter();
    const isLoggedIn = useIsLoggedIn();

    const { userAlertId, savedSearchAndAlerts } = state;

    const trackSegmentEvent = useTrackSegmentEvent();

    const onClick = (
        evt: MouseEvent<HTMLAnchorElement>,
        label?: string,
        requiresAuth = false
    ) => {
        evt.preventDefault();

        if (label) {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'search results',
                eventAction: 'click',
                eventLabel: label
            });

            const allPageLevelTaxonomy = {
                ...ANALYTICS_BASE_TAXONOMY,
                ...(taxonomy as unknown as Taxonomy),
                ...(updateUserTaxonomyKeys(
                    userTaxonomy
                ) as unknown as UserTaxonomy),
                signedInStatus: isLoggedIn ? 'signed in' : 'signed out',
                location: taxonomy?.areaName
            };

            switch (label) {
                case 'search_started':
                    trackSegmentEvent(
                        'Click Save Search',
                        allPageLevelTaxonomy as Taxonomy,
                        undefined
                    );
                    break;
                case 'edit_search':
                    trackSegmentEvent(
                        'Click Edit Search',
                        allPageLevelTaxonomy as Taxonomy,
                        undefined
                    );
                    break;
                case 'alert_started':
                    trackSegmentEvent(
                        'Click Email Alert',
                        allPageLevelTaxonomy as Taxonomy,
                        undefined
                    );
                    break;
                case 'edit_alert':
                    trackSegmentEvent(
                        'Click Enable Email Alert',
                        allPageLevelTaxonomy as Taxonomy,
                        undefined
                    );
                    break;
            }
        }

        alertLinkClickHandler({
            evt,
            router,
            requiresAuth,
            isLoggedIn
        });
    };

    const isSavedSearch = userAlertId !== null;

    if (isSavedSearch)
        return (
            <MobileAlertPanelWrapper data-testid="alert-panel-mobile">
                <ButtonLinkWithIcon
                    variant="tertiary"
                    data-testid="turn-on-alert"
                    href={savedSearchAndAlerts?.uri as string}
                    data-url={savedSearchAndAlerts?.uri as string}
                    onClick={(evt) =>
                        onClick(
                            evt,
                            savedSearchAndAlerts?.currentFrequency.value === ''
                                ? 'alert_started'
                                : 'edit_alert',
                            true
                        )
                    }
                >
                    <Icon iconId="bell" />{' '}
                    <MobileText data-testid="display-mobile-text">
                        {mobileTurnOnAlertButtonText}
                    </MobileText>
                    <TabletText data-testid="display-tablet-text">
                        {tabletTurnOnAlertButtonText}
                    </TabletText>
                </ButtonLinkWithIcon>

                <Divider />
                <ButtonLinkWithIcon
                    variant="tertiary"
                    data-testid="edit-search"
                    href={savedSearchAndAlerts?.uri as string}
                    data-url={savedSearchAndAlerts?.uri as string}
                    onClick={(evt) => onClick(evt, 'edit_search', true)}
                >
                    <Icon iconId="pencil" />{' '}
                    <MobileText>{mobileEditSearchButtonText}</MobileText>
                    <TabletText>{tabletEditSearchButtonText}</TabletText>
                </ButtonLinkWithIcon>
            </MobileAlertPanelWrapper>
        );

    return (
        <MobileAlertPanelWrapper data-testid="alert-panel-mobile">
            <ButtonLinkWithIcon
                variant="tertiary"
                data-testid="create-alert"
                href={links?.createAlert || ''}
                data-url={links?.createAlert || ''}
                onClick={(evt) => onClick(evt, 'alert_started', true)}
            >
                <Icon iconId="bell" />{' '}
                <MobileText>{mobileCreateAlertButtonText}</MobileText>
                <TabletText>{tabletCreateAlertButtonText}</TabletText>
            </ButtonLinkWithIcon>

            <Divider />

            <ButtonLinkWithIcon
                variant="tertiary"
                data-testid="save-search"
                href={links?.saveSearch || ''}
                data-url={links?.saveSearch || ''}
                onClick={(evt) => onClick(evt, 'search_started', true)}
            >
                <Icon iconId="save_outline" />{' '}
                <MobileText>{mobileSaveSearchButtonText}</MobileText>
                <TabletText>{tabletSaveSearchButtonText}</TabletText>
            </ButtonLinkWithIcon>
        </MobileAlertPanelWrapper>
    );
};
